import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { describeError } from './api';

export type Load<T> =
  { state: 'loading' } | { state: 'ready'; data: T } | { state: 'error'; message: string };

/** Loads on mount and whenever `key` changes; `reload` fetches again. */
export function useLoad<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  key: string,
): [Load<T>, () => void] {
  const [load, setLoad] = useState<Load<T>>({ state: 'loading' });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoad((current) => (current.state === 'ready' ? current : { state: 'loading' }));
    fetcher(controller.signal)
      .then((data) => setLoad({ state: 'ready', data }))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoad({ state: 'error', message: describeError(error) });
        }
      });
    return () => controller.abort();
    // `fetcher` is recreated every render; `key` names what it fetches, so it
    // alone decides when to fetch again.
  }, [key, tick]);
  return [load, useCallback(() => setTick((n) => n + 1), [])];
}

export function Loaded<T>({ load, children }: { load: Load<T>; children: (data: T) => ReactNode }) {
  if (load.state === 'loading') {
    return (
      <p className="adm-muted" aria-busy="true">
        Loading…
      </p>
    );
  }
  if (load.state === 'error') {
    return (
      <div className="alert" role="alert">
        <strong>Could not load this</strong>
        {load.message}
      </div>
    );
  }
  return <>{children(load.data)}</>;
}

type Tone = 'ok' | 'warn' | 'crit' | 'neutral';

export function Badge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`adm-badge ${tone}`}>{children}</span>;
}

export function statusTone(status: string): Tone {
  switch (status) {
    case 'ok':
    case 'active':
    case 'success':
      return 'ok';
    case 'warning':
    case 'pending':
    case 'retiring':
    case 'suspended':
      return 'warn';
    case 'missing':
    case 'mismatch':
    case 'error':
    case 'failure':
    case 'denied':
    case 'purged':
      return 'crit';
    default:
      return 'neutral';
  }
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="adm-copy"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      aria-label={`${label}: ${text.slice(0, 40)}`}
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

export function when(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/**
 * A button that runs an async action, shows progress, and reports failure.
 * `confirm`: a question asked first, for an action that is hard to take back
 * (a reset that signs someone out, a removal); No leaves everything as it was.
 */
export function ActionButton({
  run,
  children,
  tone,
  disabled,
  confirm,
}: {
  run: () => Promise<void>;
  children: ReactNode;
  tone?: 'danger' | 'ghost';
  disabled?: boolean;
  confirm?: string | undefined;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <span className="adm-action">
      <button
        type="button"
        className={`btn ${tone ?? ''}`}
        disabled={busy || disabled}
        onClick={() => {
          if (confirm && !window.confirm(confirm)) {
            return;
          }
          setBusy(true);
          setError(null);
          run()
            .catch((e: unknown) => setError(describeError(e)))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Working…' : children}
      </button>
      {error && (
        <span className="adm-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
